// ─── WalletPage.jsx ────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Wallet,
  Zap,
  ChevronRight,
  Trophy,
  ArrowDownToLine,
  ArrowUpFromLine,
  Minus,
  Coins,
  Loader2,
} from "lucide-react";
import { theme, injectTheme } from "../assets/theme";
import { api } from "../services/api";

function money(n) {
  return `${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ETB`;
}

// One entry per `type` — this is the only place you need to touch if you
// add a new transaction type later (e.g. "withdrawal" once that ships).
const TX_META = {
  deposit: {
    Icon: ArrowDownToLine,
    color: "#fb923c",
    bg: "rgba(249,115,22,0.12)",
    label: "Deposit",
    positive: true,
  },
  withdrawal: {
    Icon: ArrowUpFromLine,
    color: "#ef4444",
    bg: "rgba(239,68,68,0.14)",
    label: "Withdrawal",
    positive: false,
  },
  stake: {
    Icon: Minus,
    color: "#ef4444",
    bg: "rgba(239,68,68,0.14)",
    label: "Game Entry",
    positive: false,
  },
  prize: {
    Icon: Trophy,
    color: "#22c55e",
    bg: "rgba(34,197,94,0.13)",
    label: "Bingo Win",
    positive: true,
  },
  refund: {
    Icon: ArrowDownToLine,
    color: "#a78bfa",
    bg: "rgba(167,139,250,0.13)",
    label: "Refund",
    positive: true,
  },
  convert_in: {
    Icon: Coins,
    color: "#22c55e",
    bg: "rgba(34,197,94,0.13)",
    label: "Wallet Convert",
    positive: true,
  },
  convert_out: {
    Icon: Coins,
    color: "#ef4444",
    bg: "rgba(239,68,68,0.14)",
    label: "Wallet Convert",
    positive: false,
  },
};

function WalletCard({ label, amount, icon: Icon }) {
  return (
    <div
      style={{
        flex: 1,
        borderRadius: theme.radius.lg,
        background:
          "linear-gradient(135deg, rgba(249,115,22,0.22) 0%, rgba(217,119,6,0.18) 100%)",
        border: `1px solid ${theme.color.accentRing}`,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "28px",
          height: "28px",
          borderRadius: theme.radius.sm,
          background: theme.color.accentMuted,
          border: `1px solid ${theme.color.accentRing}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={14} color={theme.color.accentLight} />
      </div>
      <p
        style={{
          fontSize: "10px",
          fontFamily: theme.font.bodyFamily,
          fontWeight: theme.font.weightMed,
          color: theme.color.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontFamily: theme.font.family,
          fontWeight: theme.font.weightBlack,
          fontSize: "22px",
          color: theme.color.textPrimary,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {amount}
      </p>
      <span
        style={{
          position: "absolute",
          bottom: "-20px",
          right: "-20px",
          width: "80px",
          height: "80px",
          borderRadius: theme.radius.full,
          background: "rgba(249,115,22,0.12)",
          filter: "blur(20px)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

function TransactionRow({ tx }) {
  const meta = TX_META[tx.type] ?? TX_META.deposit;
  const { Icon } = meta;
  const date = new Date(tx.created_at).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "13px 14px",
        borderRadius: theme.radius.lg,
        background: theme.color.bgCard,
        border: `1px solid ${theme.color.borderCard}`,
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: theme.radius.md,
          background: meta.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={18} color={meta.color} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontSize: "13px",
            fontWeight: theme.font.weightSemi,
            fontFamily: theme.font.bodyFamily,
            color: theme.color.textSub,
            marginBottom: "3px",
          }}
        >
          {tx.note || meta.label}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <p
            style={{
              fontSize: "10px",
              color: theme.color.textDim,
              fontFamily: theme.font.bodyFamily,
            }}
          >
            {date}
          </p>
          <span
            style={{
              fontSize: "9px",
              fontWeight: 700,
              color: theme.color.accentLight,
              background: theme.color.accentMuted,
              border: `1px solid ${theme.color.accentRing}`,
              borderRadius: "9999px",
              padding: "1px 7px",
              textTransform: "uppercase",
            }}
          >
            {tx.wallet} wallet
          </span>
        </div>
      </div>

      <span
        style={{
          fontSize: "14px",
          fontWeight: theme.font.weightBold,
          fontFamily: theme.font.bodyFamily,
          fontVariantNumeric: "tabular-nums",
          color: meta.positive ? "#22c55e" : "#ef4444",
          flexShrink: 0,
        }}
      >
        {meta.positive ? "+" : "-"}
        {money(tx.amount)}
      </span>
    </div>
  );
}

export default function WalletComp() {
  const [playBalance, setPlayBalance] = useState(0);
  const [mainBalance, setMainBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const sentinelRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    injectTheme();
  }, []);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getWallet();
      setPlayBalance(data.playBalance);
      setMainBalance(data.mainBalance);
      setTransactions(data.transactions);
      setCursor(data.nextCursor);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err.message || "Could not load your wallet.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !cursor) return;
    setLoadingMore(true);
    try {
      const data = await api.getWallet(cursor);
      setTransactions((prev) => [...prev, ...data.transactions]);
      setCursor(data.nextCursor);
      setHasMore(data.hasMore);
    } catch (err) {
      console.warn("Could not load more transactions:", err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, hasMore, loadingMore]);

  useEffect(() => {
    if (!sentinelRef.current || !scrollRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root: scrollRef.current, rootMargin: "200px", threshold: 0 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        background: theme.color.bgSurface,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 24px 14px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "34px",
              height: "34px",
              borderRadius: theme.radius.md,
              background: `linear-gradient(135deg, ${theme.color.playFrom}, ${theme.color.playTo})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: theme.shadow.logo,
            }}
          >
            <Wallet size={17} color="#fff" />
          </div>
          <span
            style={{
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBlack,
              fontSize: "22px",
              color: theme.color.textPrimary,
            }}
          >
            Wallet
          </span>
        </div>
      </header>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "0 16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          scrollbarWidth: "none",
          paddingBottom: "100px",
        }}
      >
        <div style={{ display: "flex", gap: "10px" }}>
          <WalletCard
            label="Play Wallet"
            amount={loading ? "—" : money(playBalance)}
            icon={Zap}
          />
          <WalletCard
            label="Main Wallet"
            amount={loading ? "—" : money(mainBalance)}
            icon={Wallet}
          />
        </div>

        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "10px",
            }}
          >
            <span
              style={{
                width: "4px",
                height: "18px",
                borderRadius: theme.radius.full,
                background: theme.color.accent,
              }}
            />
            <h2
              style={{
                fontFamily: theme.font.family,
                fontWeight: theme.font.weightBlack,
                fontSize: "13px",
                color: theme.color.textPrimary,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Recent Transactions
            </h2>
          </div>

          {loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "40px 0",
              }}
            >
              <Loader2
                size={22}
                color={theme.color.textMuted}
                className="spin"
              />
            </div>
          )}

          {!loading && error && (
            <div
              style={{
                textAlign: "center",
                padding: "30px 16px",
                color: theme.color.textMuted,
              }}
            >
              <p style={{ marginBottom: "10px" }}>{error}</p>
              <button
                onClick={loadFirstPage}
                style={{
                  padding: "8px 16px",
                  borderRadius: theme.radius.md,
                  background: theme.color.accent,
                  border: "none",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && transactions.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "40px 16px",
                color: theme.color.textMuted,
              }}
            >
              No transactions yet — deposit or play a game to see activity here.
            </div>
          )}

          {!loading && !error && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {transactions.map((tx) => (
                <TransactionRow key={tx.id} tx={tx} />
              ))}
            </div>
          )}

          {!loading && hasMore && (
            <div
              ref={sentinelRef}
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "12px 0",
              }}
            >
              {loadingMore ? (
                <Loader2
                  size={18}
                  color={theme.color.textMuted}
                  className="spin"
                />
              ) : (
                <button
                  onClick={loadMore}
                  style={{
                    fontSize: "12px",
                    color: theme.color.textMuted,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Load more
                </button>
              )}
            </div>
          )}

          {!loading && !hasMore && transactions.length > 0 && (
            <p
              style={{
                textAlign: "center",
                fontSize: "11px",
                color: theme.color.textDim,
                padding: "8px 0",
              }}
            >
              You've reached the end of your history.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
